import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import pb from "../services/pocketbase";
import { login } from "../auth/authApi";
import { validationPopup } from "../redux/actions";

const EMAIL_CHANGE_PENDING_KEY = "uud:emailChangePending";
const EMAIL_CHANGE_PW_KEY = "uud:emailChangePw";

function safeParseJson(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function looksLikeJwt(token) {
  if (typeof token !== "string") return false;
  const t = token.trim();
  if (t.length < 40) return false;
  const parts = t.split(".");
  return parts.length === 3 && parts.every((p) => p.length > 5);
}

function getJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(base64);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function readPending() {
  const p = safeParseJson(localStorage.getItem(EMAIL_CHANGE_PENDING_KEY));
  if (!p?.email || !p?.expiresAtMs) return null;
  const expiresAtMs = Number(p.expiresAtMs);
  if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) return null;
  return { email: String(p.email), expiresAtMs };
}

function readPw() {
  const p =
    safeParseJson(localStorage.getItem(EMAIL_CHANGE_PW_KEY)) ||
    safeParseJson(sessionStorage.getItem(EMAIL_CHANGE_PW_KEY));
  if (!p?.password || !p?.expiresAtMs) return null;
  const expiresAtMs = Number(p.expiresAtMs);
  if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) return null;
  return { password: String(p.password), expiresAtMs };
}

function clearPending() {
  localStorage.removeItem(EMAIL_CHANGE_PENDING_KEY);
  localStorage.removeItem(EMAIL_CHANGE_PW_KEY);
  sessionStorage.removeItem(EMAIL_CHANGE_PW_KEY);
}

export default function ConfirmEmailChange({ token: tokenFromPath }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const [message, setMessage] = useState("");

  const token = useMemo(() => {
    const q = searchParams.get("token");
    const p = params?.token;
    return (tokenFromPath || p || q || "").trim();
  }, [params, searchParams, tokenFromPath]);

  const showTooltip = (msg, type = "info", icon = "loading") => {
    dispatch(validationPopup({ message: msg, type, icon, scopePath: "/setting" }));
  };

  const goSettingEmail = () => {
    localStorage.setItem("settingActiveSection", "email");
    navigate("/setting", { replace: true });
  };

  const confirmWithPassword = async (password) => {
    const pending = readPending();
    const pw = typeof password === "string" ? password : "";
    if (!pw.trim()) {
      showTooltip(
        "Couldn't auto-complete email change in this browser session. Please request the change again from Settings.",
        "error",
        "horror"
      );
      goSettingEmail();
      return;
    }

    try {
      await pb.collection("users").confirmEmailChange(token, pw);
    } catch (e) {
      showTooltip(
        e?.response?.message ||
          e?.message ||
          "Could not confirm the email change. The link may be expired.",
        "error",
        "horror"
      );
      goSettingEmail();
      return;
    }

    const payload = getJwtPayload(token);
    const newEmail =
      (payload?.newEmail && String(payload.newEmail)) ||
      (pending?.email && String(pending.email)) ||
      "";

    if (!newEmail) {
      clearPending();
      showTooltip(
        "Email confirmed. Please log in again to continue.",
        "success",
        "like"
      );
      setTimeout(() => navigate("/", { replace: true }), 1200);
      return;
    }

    try {
      await login(newEmail, pw);
      clearPending();
      showTooltip("Email confirmed. Your email has been updated.", "success", "like");
      setTimeout(() => goSettingEmail(), 300);
    } catch (e) {
      clearPending();
      showTooltip(
        e?.message ||
          "Email confirmed, but we couldn't re-login automatically. Please login again.",
        "info",
        "mail"
      );
      setTimeout(() => navigate("/", { replace: true }), 1200);
    }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const pw = readPw();

      if (!looksLikeJwt(token)) {
        showTooltip("Invalid confirmation link.", "error", "horror");
        goSettingEmail();
        return;
      }

      if (!pw?.password) {
        if (cancelled) return;
        showTooltip(
          "Couldn't auto-complete email change in this browser session. Please request the change again from Settings.",
          "error",
          "horror"
        );
        goSettingEmail();
        return;
      }
      await confirmWithPassword(pw.password);
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div style={{ padding: "2rem", maxWidth: 720, margin: "0 auto" }}>
      <h2 style={{ marginBottom: "0.5rem" }}>Confirming email change…</h2>
      <p style={{ opacity: 0.9 }}>
        {message || "Please wait. This will only take a moment."}
      </p>
    </div>
  );
}

